import React, { useState, useEffect } from "react";
import { supabase } from "@/integrations/supabase/client";
import { useToast } from "@/hooks/use-toast";
import { logger } from '@/lib/logger';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Badge } from "@/components/ui/badge";

interface ShowingEvent {
  id: string;
  north_id: string;
  event_type: string;
  title: string;
  scheduled_date: string;
  feedback: string | null;
  links: any;
  attachments: any;
  created_at?: string;
  updated_at?: string;
  status?: string | null;
}

interface AddEventDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  northId: string;
  event: ShowingEvent | null;
  hoverColor?: string | null;
  onSuccess: () => void;
}

export const AddEventDialog = ({
  open,
  onOpenChange,
  northId,
  event,
  hoverColor,
  onSuccess,
}: AddEventDialogProps) => {
  const [eventType, setEventType] = useState<string>('showing');
  const [status, setStatus] = useState<string>('confirmed');
  const [title, setTitle] = useState("");
  const [scheduledDate, setScheduledDate] = useState("");
  const [feedback, setFeedback] = useState("");
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [recentEvents, setRecentEvents] = useState<ShowingEvent[]>([]);
  const { toast } = useToast();

  useEffect(() => {
    if (open) {
      if (event) {
        // Edit mode - convert UTC to local time for datetime-local input
        setEventType(event.event_type);
        setStatus(event.status || 'confirmed');
        setTitle(event.title);
        
        // Times are stored as "fake UTC" (local time written as UTC).
        // Use UTC getters so the datetime-local input shows the original local time.
        const utcDate = new Date(event.scheduled_date);
        const year = utcDate.getUTCFullYear();
        const month = String(utcDate.getUTCMonth() + 1).padStart(2, '0');
        const day = String(utcDate.getUTCDate()).padStart(2, '0');
        const hours = String(utcDate.getUTCHours()).padStart(2, '0');
        const minutes = String(utcDate.getUTCMinutes()).padStart(2, '0');

        setScheduledDate(`${year}-${month}-${day}T${hours}:${minutes}`);
        setFeedback(event.feedback || "");
      } else {
        // Add mode - reset form
        setEventType('showing');
        setStatus('confirmed');
        setTitle("");
        const now = new Date();
        // Format for datetime-local (local time, no timezone)
        const year = now.getFullYear();
        const month = String(now.getMonth() + 1).padStart(2, '0');
        const day = String(now.getDate()).padStart(2, '0');
        const hours = String(now.getHours()).padStart(2, '0');
        const minutes = String(now.getMinutes()).padStart(2, '0');
        setScheduledDate(`${year}-${month}-${day}T${hours}:${minutes}`);
        setFeedback("");
      }
      fetchRecentEvents();
    }
  }, [open, event]);

  const fetchRecentEvents = async () => {
    try {
      const { data, error } = await supabase
        .from('showing_events')
        .select('*')
        .eq('north_id', northId)
        .order('scheduled_date', { ascending: false })
        .limit(6);

      if (error) throw error;
      setRecentEvents(data || []);
    } catch (error) {
      logger.error('Error fetching recent events:', error);
    }
  };

  const handleSubmit = async () => {
    if (!title.trim()) {
      toast({
        title: "Error",
        description: "Event title is required",
        variant: "destructive",
      });
      return;
    }

    setIsSubmitting(true);
    try {
      // Treat datetime-local value as UTC (consistent with "fake UTC" storage convention).
      const scheduledISO = scheduledDate + ':00.000Z';
      const eventData = {
        north_id: northId,
        event_type: eventType,
        status,
        title: title.trim(),
        showing_date: scheduledISO,
        scheduled_date: scheduledISO,
        feedback: feedback.trim() || null,
        links: [],
        attachments: [],
        updated_at: new Date().toISOString(),
      };

      if (event) {
        // Update existing event
        const { error } = await supabase
          .from('showing_events')
          .update(eventData)
          .eq('id', event.id);

        if (error) throw error;

        toast({
          title: "Event Updated",
          description: "Event updated successfully",
        });
      } else {
        // Create new event
        const { error } = await supabase
          .from('showing_events')
          .insert(eventData);

        if (error) throw error;

        toast({
          title: "Event Added",
          description: "Event created successfully",
        });
      }

      onSuccess();
    } catch (error) {
      logger.error('Error saving event:', error);
      toast({
        title: "Error",
        description: "Failed to save event",
        variant: "destructive",
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleDateString('en-US', {
      month: 'short',
      day: 'numeric',
      year: 'numeric',
    });
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle>{event ? 'Edit Event' : 'Add Event'}</DialogTitle>
        </DialogHeader>

        <div className="space-y-4 py-4">
          {/* Event Type, Status & Date */}
          <div className="grid grid-cols-2 gap-4">
            <div className="space-y-2">
              <Label>Event Type</Label>
              <Select value={eventType} onValueChange={(value: string) => setEventType(value)}>
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="showing">Showing</SelectItem>
                  <SelectItem value="open_house">Open House</SelectItem>
                </SelectContent>
              </Select>
            </div>

            <div className="space-y-2">
              <Label>Status</Label>
              <Select value={status} onValueChange={(value: string) => setStatus(value)}>
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="requested">Requested</SelectItem>
                  <SelectItem value="confirmed">Confirmed</SelectItem>
                  <SelectItem value="cancelled">Cancelled</SelectItem>
                  <SelectItem value="completed">Completed</SelectItem>
                </SelectContent>
              </Select>
            </div>
          </div>

          <div className="space-y-2">
            <Label>Date & Time *</Label>
            <Input
              type="datetime-local"
              value={scheduledDate}
              onChange={(e) => setScheduledDate(e.target.value)}
            />
          </div>

          {/* Title */}
          <div className="space-y-2">
            <Label>Title *</Label>
            <Input
              placeholder="e.g., Monday's Showing, Weekend Open House"
              value={title}
              onChange={(e) => setTitle(e.target.value)}
            />
          </div>

          {/* Feedback */}
          <div className="space-y-2">
            <Label>Feedback</Label>
            <Textarea
              placeholder="Enter buyer feedback or notes from the event..."
              value={feedback}
              onChange={(e) => setFeedback(e.target.value)}
              className="min-h-[100px]"
            />
          </div>

          {/* Recent Events Reference */}
          {!event && recentEvents.length > 0 && (
            <div className="space-y-2 pt-4 border-t">
              <Label className="text-sm text-muted-foreground">Recent Events (for reference)</Label>
              <div className="space-y-2">
                {recentEvents.map((recentEvent) => (
                  <div
                    key={recentEvent.id}
                    className="flex items-center justify-between p-2 bg-muted/30 rounded"
                  >
                    <div className="flex items-center gap-2">
                      <Badge variant={recentEvent.event_type === 'showing' ? 'default' : 'secondary'} className="text-xs">
                        {recentEvent.event_type === 'showing' ? 'Showing' : recentEvent.event_type === 'open_house' ? 'Open House' : recentEvent.event_type}
                      </Badge>
                      <span className="text-sm font-medium">{recentEvent.title}</span>
                    </div>
                    <span className="text-xs text-muted-foreground">
                      {formatDate(recentEvent.scheduled_date)}
                    </span>
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>

        {/* Action Buttons */}
        <div className="flex justify-end gap-2 pt-4 border-t">
          <Button
            variant="outline"
            onClick={() => onOpenChange(false)}
            disabled={isSubmitting}
            style={{ transition: 'background-color 0.2s ease-in-out' }}
            onMouseEnter={(e) => {
              if (hoverColor) {
                e.currentTarget.style.backgroundColor = hoverColor;
              }
            }}
            onMouseLeave={(e) => {
              e.currentTarget.style.backgroundColor = 'transparent';
            }}
          >
            Cancel
          </Button>
          <Button
            onClick={handleSubmit}
            disabled={isSubmitting}
          >
            {isSubmitting ? "Saving..." : event ? "Update Event" : "Add Event"}
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
};